// VOY-485 (rebuild) — Travel preferences (Settings tab) — 6-FIELD CATEGORICAL.
//
// DECLARED INPUT. Mirrors the live profile's `TravelPreferencesCard`:
// six categorical fields (Budget / Style mirror / Pace / Accommodation /
// Group / Planning) each a single-select toggle group. These feed
// VOY-438's preference resolver as the `source='declared'` tier — without
// this UI, the resolver has nothing to resolve for those dimensions.
//
// Storage: `user_preferences` preference_type='travel' (same as the live
// card writes; the resolver reads this slice).
//
// The "Style" row here is a READ-ONLY mirror of StyleInterestsSection's
// selection count — editing happens in that other card (live behavior
// preserved).

import { useEffect, useState } from 'react'
import {
  BUDGET_OPTIONS,
  PACE_OPTIONS,
  ACCOMMODATION_OPTIONS,
  GROUP_OPTIONS,
  PLANNING_OPTIONS,
  humanize,
  readStyleInterests,
} from '@/components/profile/helpers/preferences-helpers'
import type { TravelPreferences } from '@/types/profile'

export interface TravelPreferencesCategoricalSectionProps {
  preferences: TravelPreferences
  onSave: (next: TravelPreferences) => Promise<void>
}

type OwnedFields = Pick<TravelPreferences, 'budget_level' | 'travel_pace' | 'accommodation' | 'group_type' | 'planning_style'>

function pickOwned(p: TravelPreferences): OwnedFields {
  return {
    budget_level: p.budget_level,
    travel_pace: p.travel_pace,
    accommodation: p.accommodation,
    group_type: p.group_type,
    planning_style: p.planning_style,
  }
}

export function TravelPreferencesCategoricalSection({ preferences, onSave }: TravelPreferencesCategoricalSectionProps) {
  const [draft, setDraft] = useState<OwnedFields>(() => pickOwned(preferences))
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const styleCount = readStyleInterests(preferences).length

  // Re-seed the draft from the parent whenever preferences update AND
  // we're not actively editing. Without this the draft holds a stale
  // snapshot from first mount; a reload from the parent (e.g. after the
  // user edited Style+Interests in a sibling card) would put outdated
  // values in the edit form the next time it opened. Mirrors the live
  // TravelPreferencesCard pattern.
  useEffect(() => {
    if (!editing) setDraft(pickOwned(preferences))
  }, [preferences, editing])

  async function commit() {
    setSaving(true)
    try {
      // Spread preferences FIRST so we don't clobber sibling fields
      // (style_interests, keywords) that this card doesn't own.
      await onSave({ ...preferences, ...draft })
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  function cancel() {
    setDraft(pickOwned(preferences))
    setEditing(false)
  }

  return (
    <section className="card" aria-labelledby="travel-prefs-h">
      <div className="card-h">
        <h3 id="travel-prefs-h">Travel preferences</h3>
        {!editing ? (
          <button type="button" className="btn-ghost" style={{ padding: '5px 12px', fontSize: 11.5 }} onClick={() => setEditing(true)}>Edit</button>
        ) : (
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn-ghost" style={{ padding: '5px 12px', fontSize: 11.5 }} onClick={cancel} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary" style={{ padding: '5px 12px', fontSize: 11.5 }} onClick={commit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <dl className="stack sm" style={{ gap: 10, margin: 0 }}>
          <ReadRow label="Budget" value={humanize(preferences.budget_level)} />
          <ReadRow label="Style" value={styleCount === 0 ? '' : `${styleCount} selected`} />
          <ReadRow label="Pace" value={humanize(preferences.travel_pace)} />
          <ReadRow label="Accommodation" value={humanize(preferences.accommodation)} />
          <ReadRow label="Group" value={humanize(preferences.group_type)} />
          <ReadRow label="Planning" value={humanize(preferences.planning_style)} />
        </dl>
      ) : (
        <div className="stack" style={{ gap: 14 }}>
          <EditRow
            label="Budget"
            value={draft.budget_level}
            options={BUDGET_OPTIONS}
            onChange={(v) => setDraft((d) => ({ ...d, budget_level: (v || undefined) as TravelPreferences['budget_level'] }))}
          />
          <StyleHint count={styleCount} />
          <EditRow
            label="Pace"
            value={draft.travel_pace}
            options={PACE_OPTIONS}
            onChange={(v) => setDraft((d) => ({ ...d, travel_pace: (v || undefined) as TravelPreferences['travel_pace'] }))}
          />
          <EditRow
            label="Accommodation"
            value={draft.accommodation}
            options={ACCOMMODATION_OPTIONS}
            onChange={(v) => setDraft((d) => ({ ...d, accommodation: (v || undefined) as TravelPreferences['accommodation'] }))}
          />
          <EditRow
            label="Group"
            value={draft.group_type}
            options={GROUP_OPTIONS}
            onChange={(v) => setDraft((d) => ({ ...d, group_type: (v || undefined) as TravelPreferences['group_type'] }))}
          />
          <EditRow
            label="Planning"
            value={draft.planning_style}
            options={PLANNING_OPTIONS}
            onChange={(v) => setDraft((d) => ({ ...d, planning_style: (v || undefined) as TravelPreferences['planning_style'] }))}
          />
        </div>
      )}
    </section>
  )
}

function ReadRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="row between" style={{ alignItems: 'baseline', padding: '6px 0', borderBottom: '1px solid var(--v6-hair)' }}>
      <dt style={{ color: 'var(--v6-fg-muted)', fontSize: 13 }}>{label}</dt>
      <dd style={{ margin: 0, color: value ? 'var(--v6-fg-strong)' : 'var(--v6-fg-muted)', fontStyle: value ? 'normal' : 'italic', fontSize: 13.5 }}>
        {value || 'Set'}
      </dd>
    </div>
  )
}

function EditRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string | undefined
  options: ReadonlyArray<{ value: string; label: string }>
  onChange: (v: string) => void
}) {
  return (
    <div>
      <div className="lbl" style={{ marginBottom: 6 }}>{label}</div>
      <div className="pill-cloud">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={value === opt.value ? 'choice-pill on' : 'choice-pill'}
            onClick={() => onChange(opt.value)}
            aria-pressed={value === opt.value}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function StyleHint({ count }: { count: number }) {
  return (
    <div style={{ padding: '8px 12px', border: '1px dashed var(--v6-hair-strong)', borderRadius: 10 }}>
      <div className="lbl">Style</div>
      <div style={{ fontSize: 12, color: 'var(--v6-fg-body)', marginTop: 4 }}>
        {count === 0
          ? 'Edit your Style + interests on the Public profile tab.'
          : `${count} selected — edit on the Public profile tab.`}
      </div>
    </div>
  )
}
